import { Currency, Grid, Input, Label } from '@mairistem/composants';
import {
	Combobox,
	type Etiquette,
	type UserRole,
} from '@mairistem/finances-utils';
import React from 'react';
import StatutMontantSimulation from '../../../../constants/simulation/statutMontantSimulation';
import TypePeriode from '../../../../constants/simulation/typePeriode';
import type MontantSimulation from '../../../../interfaces/simulation/montantSimulation';
import { getPeriodeFromMontantColumn } from '../../../../utils/feuilleSaisie/imputation';
import isInputDisabled from '../../../../utils/simulation/input';

interface MontantsSimulationModalContentItemProps {
	EtiquettesInput: any;
	montantSimulation: MontantSimulation;
	onChange: (data: Partial<MontantSimulation>) => void;
	onChangeCommentaire: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChangeMontant: (
		differenceMontant: number,
		typePeriode: TypePeriode,
	) => void;
	transfertCpta: boolean;
	typePeriode: TypePeriode;
	userRole: UserRole;
}

const statutOptions = [
	{
		content: <Label color="olive" content="Accepté" />,
		'data-testid': `statut${StatutMontantSimulation.ACCEPTE}`,
		key: StatutMontantSimulation.ACCEPTE,
		text: 'Accepté',
		value: StatutMontantSimulation.ACCEPTE,
	},
	{
		content: <Label color="teal" content="À arbitrer" />,
		'data-testid': `statut${StatutMontantSimulation.A_ARBITRER}`,
		key: StatutMontantSimulation.A_ARBITRER,
		text: 'À arbitrer',
		value: StatutMontantSimulation.A_ARBITRER,
	},
	{
		content: <Label color="red" content="Refusé" />,
		'data-testid': `statut${StatutMontantSimulation.REFUSE}`,
		key: StatutMontantSimulation.REFUSE,
		text: 'Refusé',
		value: StatutMontantSimulation.REFUSE,
	},
];

const renderStatutLabel = (montantStatut: string) =>
	statutOptions.find((statutOption) => statutOption.value === montantStatut)
		.content;

const MontantsSimulationModalContentItem = ({
	EtiquettesInput,
	montantSimulation,
	onChange,
	onChangeCommentaire,
	onChangeMontant,
	transfertCpta,
	typePeriode,
	userRole,
}: MontantsSimulationModalContentItemProps) => {
	const handleBlurCommentaire = (e: React.FocusEvent<HTMLInputElement>) => {
		// Optimisation: si meme valeur qu'avant on ne fait pas le onChange
		if (montantSimulation.commentaire === e.currentTarget.value) return;

		// Mise a jour du commentaire sur le montant de simulation
		onChange({ commentaire: e.currentTarget.value });
		// Mise a jour du commentaire dans la treeView
		onChangeCommentaire(e);
	};

	const handleBlurMontant = (
		{ currentTarget: { name } }: React.FocusEvent<HTMLInputElement>,
		montant: number,
	) => {
		// Optimisation: si meme valeur qu'avant on ne fait pas le onChange
		if (montantSimulation[name] === montant) return;

		// Mise a jour du montant sur le montant de simulation
		onChange({ [name]: montant });

		const periodeFromMontant = getPeriodeFromMontantColumn(name);

		// Mise a jour du montant dans la TreeView uniquement si on est sur la periode
		if (
			periodeFromMontant === typePeriode &&
			montantSimulation.statut !== StatutMontantSimulation.REFUSE
		) {
			onChangeMontant(montant - (montantSimulation[name] ?? 0), typePeriode);
		}
	};

	const handleChangeStatut = (
		_e: React.SyntheticEvent<HTMLElement, Event>,
		{ value }: { value: string },
	) => {
		let differenceMontant = 0;
		const oldStatut = montantSimulation.statut;

		switch (typePeriode) {
			case TypePeriode.DEMANDE:
				if (oldStatut === StatutMontantSimulation.REFUSE) {
					differenceMontant = montantSimulation.demande ?? 0;
				} else if (value === StatutMontantSimulation.REFUSE) {
					differenceMontant = -(montantSimulation.demande ?? 0);
				}
				break;
			case TypePeriode.PROPOSE:
				switch (value) {
					case StatutMontantSimulation.A_ARBITRER:
					case StatutMontantSimulation.REFUSE:
						// En a arbitrer/refuse, on remet la proposition a 0
						differenceMontant = -(montantSimulation.prop ?? 0);
						break;
					case StatutMontantSimulation.ACCEPTE:
						// En accepte, on copie la demande dans la proposition
						differenceMontant =
							(montantSimulation.demande ?? 0) - (montantSimulation.prop ?? 0);
						break;
					default:
						break;
				}
				break;
			case TypePeriode.VOTE:
				if (oldStatut === StatutMontantSimulation.REFUSE) {
					differenceMontant = montantSimulation.vote ?? 0;
				} else if (value === StatutMontantSimulation.REFUSE) {
					differenceMontant = -(montantSimulation.vote ?? 0);
				}
				break;
			default:
				break;
		}

		onChangeMontant(differenceMontant, typePeriode);

		onChange({ statut: value });
	};

	const statut = montantSimulation.statut ?? StatutMontantSimulation.A_ARBITRER;

	return (
		<Grid.Row>
			<Grid.Column verticalAlign="middle" width={2}>
				<Combobox
					data-testid="statutCombobox"
					disabled={isInputDisabled(transfertCpta, typePeriode, userRole)}
					onChange={handleChangeStatut}
					options={statutOptions}
					// @ts-expect-error we can render JSX and not only string
					text={() => renderStatutLabel(statut)}
					value={statut}
				/>
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={2}>
				<Currency
					data-testid="demandeInput"
					disabled={
						isInputDisabled(transfertCpta, typePeriode, userRole, true) ||
						statut === StatutMontantSimulation.ACCEPTE
					}
					name="demande"
					onBlur={handleBlurMontant}
					value={montantSimulation.demande}
				/>
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={2}>
				<Currency
					data-testid="propInput"
					disabled={
						isInputDisabled(transfertCpta, typePeriode, userRole) ||
						statut !== StatutMontantSimulation.ACCEPTE
					}
					name="prop"
					onBlur={handleBlurMontant}
					value={montantSimulation.prop}
				/>
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={2}>
				<Currency
					data-testid="voteInput"
					disabled={
						isInputDisabled(transfertCpta, typePeriode, userRole) ||
						typePeriode !== TypePeriode.VOTE
					}
					name="vote"
					onBlur={handleBlurMontant}
					value={montantSimulation.vote}
				/>
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={3}>
				<Input
					disabled={isInputDisabled(transfertCpta, typePeriode, userRole, true)}
					fluid
					onBlur={handleBlurCommentaire}
					value={montantSimulation.commentaire}
				>
					<input data-testid="commentaireInput" maxLength={255} />
				</Input>
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={2}>
				{montantSimulation.type || 'Aucune origine'}
			</Grid.Column>
			<Grid.Column verticalAlign="middle" width={3}>
				<EtiquettesInput
					dropdownProps={{
						disabled: isInputDisabled(
							transfertCpta,
							typePeriode,
							userRole,
							true,
						),
					}}
					onChange={(data: { etiquettes: Etiquette[] }) =>
						onChange({ etiquettes: data.etiquettes })
					}
					selectedEtiquettes={montantSimulation.etiquettes}
				/>
			</Grid.Column>
		</Grid.Row>
	);
};

export default MontantsSimulationModalContentItem;
